import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useLoaderData,
  useNavigation,
  useSearchParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { CompareDomainSelector } from "@/components/gsc/CompareDomainSelector";
import { CompareMetricSelector } from "@/components/gsc/CompareMetricSelector";
import { CompareMetricsCards } from "@/components/gsc/CompareMetricsCards";
import { CompareTrendChart } from "@/components/gsc/CompareTrendChart";
import { GscSubNav } from "@/components/gsc/GscSubNav";
import {
  readStoredCompareDomains,
  writeStoredCompareDomains,
} from "@/lib/gsc-compare-domains-storage";
import { queryClient } from "@/lib/queryClient";
import {
  EMPTY_GSC_FILTER_OPTIONS,
  GSC_DEFAULT_COMPARE_METRIC,
  GSC_DEFAULT_PAGE_SIZE,
  GSC_MAX_COMPARE_DOMAINS,
  type GscCompareMetric,
} from "@/types/gsc";
import {
  fetchGscCompare,
  fetchGscDomainOverview,
  fetchGscFilterOptions,
  getGscCompareQueryKey,
  getGscDomainOverviewQueryKey,
  GSC_FILTER_OPTIONS_QUERY_KEY,
  GSC_FILTER_OPTIONS_STALE_TIME,
  GSC_STATS_STALE_TIME,
  parseGscCompareDomains,
  parseGscCompareMetric,
  parseGscCompareParamsFromUrl,
  useGscCompare,
  useGscDomainOverview,
  useGscFilterOptions,
  type GscCompareParams,
  type GscDomainOverviewParams,
} from "@/hooks/useGscData";

export type GscCompareLoaderData = GscCompareParams;

const writeCompareDomainsToSearchParams = (
  params: URLSearchParams,
  domains: readonly string[],
): void => {
  params.delete("domain");
  params.delete("domains");

  const capped = (Array.isArray(domains) ? domains : []).slice(
    0,
    GSC_MAX_COMPARE_DOMAINS,
  );

  for (const domain of capped) {
    params.append("domain", domain);
  }
};

const buildTopTrafficSeedParams = (
  params: Pick<GscCompareParams, "dateRange" | "customFrom" | "customTo">,
): GscDomainOverviewParams => ({
  dateRange: params.dateRange,
  customFrom: params.customFrom,
  customTo: params.customTo,
  // Seeds the Top 5 suggestion from overall traffic, so Detail's filter chips
  // deliberately do not apply here.
  activeFilters: {},
  page: 1,
  // GscPageSize is 15|50|100 — fetch a page and slice to Top 5 by clicks.
  pageSize: GSC_DEFAULT_PAGE_SIZE,
  sortBy: "clicks",
  sortDir: "desc",
});

export async function gscCompareLoader({
  request,
}: LoaderFunctionArgs): Promise<GscCompareLoaderData> {
  const url = new URL(request.url);
  const params = parseGscCompareParamsFromUrl(url);
  const domains = Array.isArray(params.domains) ? params.domains : [];
  const needsTrafficSeed =
    domains.length === 0 && readStoredCompareDomains() == null;

  await Promise.all([
    domains.length > 0
      ? queryClient
          .prefetchQuery({
            queryKey: getGscCompareQueryKey({ ...params, domains }),
            queryFn: () => fetchGscCompare({ ...params, domains }),
            staleTime: GSC_STATS_STALE_TIME,
          })
          .catch(() => undefined)
      : needsTrafficSeed
        ? queryClient
            .prefetchQuery({
              queryKey: getGscDomainOverviewQueryKey(
                buildTopTrafficSeedParams(params),
              ),
              queryFn: () =>
                fetchGscDomainOverview(buildTopTrafficSeedParams(params)),
              staleTime: GSC_STATS_STALE_TIME,
            })
            .catch(() => undefined)
        : Promise.resolve(),
    queryClient
      .prefetchQuery({
        queryKey: GSC_FILTER_OPTIONS_QUERY_KEY,
        queryFn: fetchGscFilterOptions,
        staleTime: GSC_FILTER_OPTIONS_STALE_TIME,
      })
      .catch(() => undefined),
  ]);

  return { ...params, domains };
}

export default function GscCompare() {
  const loaderParams = useLoaderData() as GscCompareLoaderData;
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasAutoFilled = useRef(false);

  const { data: filterOptions, isPending: isFilterOptionsPending } =
    useGscFilterOptions();

  const availableDomains = useMemo((): string[] => {
    const fromFilters = filterOptions?.domain;
    if (Array.isArray(fromFilters)) return fromFilters;
    return Array.isArray(EMPTY_GSC_FILTER_OPTIONS.domain)
      ? EMPTY_GSC_FILTER_OPTIONS.domain
      : [];
  }, [filterOptions?.domain]);

  const parsedUrlDomains = useMemo(
    () => parseGscCompareDomains(searchParams),
    [searchParams],
  );

  const selectedDomains = useMemo(() => {
    if (availableDomains.length === 0) return parsedUrlDomains;
    const allow = new Set(availableDomains);
    return parsedUrlDomains.filter((domain) => allow.has(domain));
  }, [parsedUrlDomains, availableDomains]);

  const activeMetric = useMemo(
    () => parseGscCompareMetric(searchParams),
    [searchParams],
  );

  const compareParams = useMemo(
    (): GscCompareParams => ({
      ...loaderParams,
      domains: selectedDomains,
      metric: activeMetric,
    }),
    [loaderParams, selectedDomains, activeMetric],
  );

  const topTrafficSeedParams = useMemo(
    () => buildTopTrafficSeedParams(compareParams),
    [compareParams],
  );

  const needsTrafficSeed =
    selectedDomains.length === 0 && readStoredCompareDomains() == null;

  const { data: compareData, isPending } = useGscCompare(compareParams);
  const {
    data: topOverviewData,
    isPending: isTopOverviewPending,
    isFetched: isTopOverviewFetched,
  } = useGscDomainOverview(topTrafficSeedParams, {
    enabled: selectedDomains.length === 0,
  });

  const topDomainsByTraffic = useMemo((): string[] => {
    const rows = topOverviewData?.rows;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => row.domain)
      .filter(Boolean)
      .slice(0, GSC_MAX_COMPARE_DOMAINS);
  }, [topOverviewData?.rows]);

  const handleDomainsChange = useCallback(
    (nextDomains: string[]) => {
      writeStoredCompareDomains(nextDomains);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeCompareDomainsToSearchParams(next, nextDomains);
        return next;
      });
    },
    [setSearchParams],
  );

  const handleMetricChange = useCallback(
    (metric: GscCompareMetric) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (metric === GSC_DEFAULT_COMPARE_METRIC) {
          next.delete("metric");
        } else {
          next.set("metric", metric);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Session hydration: empty URL → restore storage, else Top-5 by traffic (overview).
  useEffect(() => {
    if (hasAutoFilled.current) return;
    if (availableDomains.length === 0) return;

    if (selectedDomains.length > 0) {
      hasAutoFilled.current = true;
      writeStoredCompareDomains(selectedDomains);
      return;
    }

    const stored = readStoredCompareDomains();
    if (stored && stored.length > 0) {
      hasAutoFilled.current = true;
      handleDomainsChange(stored);
      return;
    }

    if (!isTopOverviewFetched || isTopOverviewPending) return;

    hasAutoFilled.current = true;

    if (topDomainsByTraffic.length === 0) return;

    handleDomainsChange(
      topDomainsByTraffic.slice(0, GSC_MAX_COMPARE_DOMAINS),
    );
  }, [
    availableDomains.length,
    handleDomainsChange,
    isTopOverviewFetched,
    isTopOverviewPending,
    selectedDomains,
    topDomainsByTraffic,
  ]);

  const isRouteLoading = navigation.state === "loading";
  const hasSelectedDomains = selectedDomains.length > 0;
  const isHydrating =
    !hasSelectedDomains &&
    (isFilterOptionsPending ||
      availableDomains.length === 0 ||
      (needsTrafficSeed && !isTopOverviewFetched));
  const isMetricsLoading =
    isHydrating || (hasSelectedDomains && (isRouteLoading || isPending));

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">GSC Compare</h1>
        <p className="text-sm text-muted-foreground">
          Side-by-side KPIs for up to five domains.
        </p>
      </div>

      <GscSubNav />

      <CompareDomainSelector
        selectedDomains={selectedDomains}
        availableDomains={availableDomains}
        onChange={handleDomainsChange}
        isLoading={
          (isFilterOptionsPending && availableDomains.length === 0) ||
          isHydrating
        }
      />

      <CompareMetricSelector
        activeMetric={activeMetric}
        onChange={handleMetricChange}
      />

      <CompareMetricsCards
        data={compareData}
        selectedDomains={selectedDomains}
        availableDomains={availableDomains}
        activeMetric={activeMetric}
        isLoading={isMetricsLoading}
      />

      <CompareTrendChart
        selectedDomains={selectedDomains}
        availableDomains={availableDomains}
        activeMetric={activeMetric}
        dateRange={compareParams.dateRange}
        customFrom={compareParams.customFrom}
        customTo={compareParams.customTo}
      />
    </div>
  );
}
